import React, { useMemo, useState } from "react";
import { CPInput } from "../CPInput";
import { CPDropdown } from "../CPDropdown";
import { usePermissions } from "../../hooks/usePermissions";
import {
  DEDUCTION_TYPE_OPTIONS,
  RECORD_KIND_OPTIONS,
  SURFACE_TYPE_OPTIONS,
  addMeasurement,
  formatSurfaceType,
  updateMeasurement,
} from "../../helpers/estimates";
import {
  emptyMeasurementEntry,
  formatMeasurementArea,
  measurementEntryFromRow,
  resolveMeasurementDimensions,
} from "../../helpers/measurementModel";
import { EstimateBottomSheet } from "./EstimateBottomSheet";
import { MeasurementDimensionFields } from "./MeasurementDimensionFields";

/**
 * Deterministic default label so most entries need no typing: "Wall 1",
 * "Floor 1", "Deduction 1", etc. — based on how many existing sections
 * already share this surface/kind. Purely a UI convenience; the name field
 * remains fully editable/overridable and is still required and persisted
 * exactly as before (no schema change, no calculation change).
 */
function computeDefaultMeasurementName(surfaceType, recordKind, existingSections) {
  if (recordKind === "deduction") {
    const count = existingSections.filter((s) => s.record_kind === "deduction").length;
    return `Deduction ${count + 1}`;
  }

  const label = formatSurfaceType(surfaceType) || "Area";
  const count = existingSections.filter(
    (s) => s.record_kind !== "deduction" && s.surface_type === surfaceType,
  ).length;
  return `${label} ${count + 1}`;
}

function emptySectionForm() {
  return {
    name: "",
    recordKind: "area",
    surfaceType: "floor",
    surfaceLabel: "",
    deductionType: "toilet",
    appliesToSurfaceType: "floor",
    ...emptyMeasurementEntry(),
  };
}

function sectionFormFromRow(section) {
  return {
    name: section.name,
    recordKind: section.record_kind,
    surfaceType: section.surface_type,
    surfaceLabel: section.surface_label || "",
    deductionType: section.deduction_type || "toilet",
    appliesToSurfaceType: section.applies_to_surface_type || "floor",
    ...measurementEntryFromRow(section),
  };
}

/**
 * Focused Add/Edit Measurement entry surface.
 *
 * Common-case-first layout: Surface, Length/Width, a calculated-area
 * preview, then a prefilled/overridable Label. Kind (Area/Deduction) and
 * deduction targeting live behind "More Options" since Deduction is the
 * less-common exception — every existing field and validation rule is
 * still present, just progressively disclosed.
 *
 * PRESERVES THE EXISTING AUTHORITATIVE CALCULATION ARCHITECTURE:
 * length_in x width_in are the only values persisted. area_sqft remains a
 * Postgres GENERATED column and surface_totals remain DB-trigger-maintained
 * (see addMeasurement/updateMeasurement in helpers/estimates.js — unchanged
 * by this component). The "Calculated area" shown below is a client-side
 * PREVIEW only, mirroring the DB formula purely for immediate user feedback
 * before save; it is never sent to the server and is replaced by the real
 * persisted value on reload.
 */
export function MeasurementFormSheet({
  estimateFeatureId,
  section = null,
  existingSections = [],
  onClose,
  onSaved,
  onPersist,
}) {
  const { actor } = usePermissions();
  const [form, setForm] = useState(() =>
    section
      ? sectionFormFromRow(section)
      : {
          ...emptySectionForm(),
          name: computeDefaultMeasurementName("floor", "area", existingSections),
        },
  );
  const [nameTouched, setNameTouched] = useState(Boolean(section));
  const [showMoreOptions, setShowMoreOptions] = useState(
    Boolean(section) && section.record_kind === "deduction",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function update(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (!nameTouched && !section && (field === "surfaceType" || field === "recordKind")) {
        next.name = computeDefaultMeasurementName(
          next.surfaceType,
          next.recordKind,
          existingSections,
        );
      }
      return next;
    });
  }

  function updateName(value) {
    setNameTouched(true);
    update("name", value);
  }

  const previewArea = useMemo(() => {
    try {
      const dimensions = resolveMeasurementDimensions(form);
      return formatMeasurementArea({
        length_in: dimensions.lengthIn,
        width_in: dimensions.widthIn,
        dimension_unit: dimensions.dimensionUnit,
        area_unit: dimensions.areaUnit,
      });
    } catch {
      return "";
    }
  }, [form]);

  async function handleSave() {
    if (!form.name.trim()) {
      setError("Enter a measurement name.");
      return;
    }
    try {
      resolveMeasurementDimensions(form);
    } catch (err) {
      setError(err.message || "Enter length and width.");
      return;
    }
    if (form.surfaceType === "other" && !form.surfaceLabel.trim()) {
      setError("Enter a label for other surface type.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (onPersist) {
        await onPersist(form);
      } else if (section) {
        await updateMeasurement(actor, section.section_id, form);
      } else {
        await addMeasurement(actor, estimateFeatureId, form);
      }
      await onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Unable to save measurement.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <EstimateBottomSheet
      title={section ? "Edit Measurement" : "Add Measurement"}
      onClose={onClose}
      onPrimary={handleSave}
      primaryLabel={section ? "Save Changes" : "Add Measurement"}
      primaryBusy={saving}
      error={error}
    >
      <CPDropdown
        label="Surface"
        value={form.surfaceType}
        onChange={(value) => update("surfaceType", value)}
        options={SURFACE_TYPE_OPTIONS}
      />

      {form.surfaceType === "other" && (
        <CPInput
          label="Surface Label"
          value={form.surfaceLabel}
          onChange={(value) => update("surfaceLabel", value)}
        />
      )}

      <MeasurementDimensionFields
        form={form}
        onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
      />

      {previewArea ? (
        <div className="est-sheet-calc-preview">
          <div className="est-sheet-calc-preview-label">
            Calculated Area (preview)
          </div>
          <div className="est-sheet-calc-preview-value">
            {previewArea}
          </div>
        </div>
      ) : null}

      <CPInput
        label="Label"
        value={form.name}
        onChange={updateName}
        placeholder="Back Wall"
        required
      />

      <button
        type="button"
        className="est-sheet-choice-row"
        onClick={() => setShowMoreOptions((v) => !v)}
      >
        <span>More Options</span>
        <span aria-hidden="true">{showMoreOptions ? "︿" : "﹀"}</span>
      </button>

      {showMoreOptions && (
        <>
          <CPDropdown
            label="Kind"
            value={form.recordKind}
            onChange={(value) => update("recordKind", value)}
            options={RECORD_KIND_OPTIONS}
          />

          {form.recordKind === "deduction" && (
            <>
              <CPDropdown
                label="Deduction Type"
                value={form.deductionType}
                onChange={(value) => update("deductionType", value)}
                options={DEDUCTION_TYPE_OPTIONS}
              />
              <CPDropdown
                label="Applies To Surface"
                value={form.appliesToSurfaceType}
                onChange={(value) => update("appliesToSurfaceType", value)}
                options={SURFACE_TYPE_OPTIONS}
              />
            </>
          )}
        </>
      )}
    </EstimateBottomSheet>
  );
}
